import React, { useState, useEffect } from 'react';
import CollapsibleDiv from 'react-collapse';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIsVisualizationAvailable } from '~SpectateStore/hooks/scoreboardHooks';
import { useIsStreamingAvailable } from '~SpectateStore/hooks/eventsHooks';
import FeedStreaming from './FeedStreaming';
import FeedVisualization from './FeedVisualization';
import './styles/scoreboard.scss';

const VIEW_MODE = {
    HIDDEN: 'hidden',
    STREAMING: 'streaming',
    VISUALIZATION: 'visualization',
};

const ScoreboardFeedToggle = ({ eventId }) => {
    const [viewModes, setViewModes] = useState([VIEW_MODE.HIDDEN]);
    const isStreamingAvailable = useIsStreamingAvailable(eventId);
    const isVisualizationAvailable = useIsVisualizationAvailable(eventId);
    const [isVisualizationReady, setIsVisualizationReady] = useState(false);
    const toggleClassNames = classNames('toggle', {
        [`toggle--${viewModes[1]}-icon`]: isStreamingAvailable,
        'toggle--no-streaming': !isStreamingAvailable,
        'toggle--up-arrow': !isStreamingAvailable && viewModes[0] === VIEW_MODE.HIDDEN,
        'toggle--down-arrow': !isStreamingAvailable && viewModes[0] !== VIEW_MODE.HIDDEN,
    });

    const rotateViewModes = () => {
        const [current, ...remaining] = viewModes;

        setViewModes([...remaining, current]);
    };

    useEffect(() => {
        // Initial order of the view modes
        // If visualisations are available then they should be shown on page load and streaming should come next if it available.
        // If visualisations are not available but streaming is then the page should load with the 'hidden' view mode.

        let initialViewModes = [];

        if (isStreamingAvailable) {
            initialViewModes.push(VIEW_MODE.STREAMING);
        }

        if (isVisualizationAvailable) {
            initialViewModes = [VIEW_MODE.VISUALIZATION, ...initialViewModes, VIEW_MODE.HIDDEN];
        } else {
            initialViewModes.unshift(VIEW_MODE.HIDDEN);
        }

        setViewModes(initialViewModes);
    }, [isStreamingAvailable, isVisualizationAvailable]);

    const renderFeed = () => {
        switch (viewModes[0]) {
            case VIEW_MODE.STREAMING:
                return (
                    <FeedStreaming
                        eventId={eventId}
                    />
                );
            case VIEW_MODE.VISUALIZATION:
                return (
                    <FeedVisualization
                        eventId={eventId}
                        updateStatusCallback={isError => {
                            if (isError) {
                                // Remove visualization from the rotation
                                setViewModes(viewModes.filter(mode => mode !== VIEW_MODE.VISUALIZATION));
                            } else {
                                setIsVisualizationReady(true);
                            }
                        }}
                    />
                );
            default:
                return <></>;
        }
    };

    const toggleGTM = () => {
        const scoreboardGTM = 'inplay-scoreboard;';

        if (viewModes[1] === VIEW_MODE.VISUALIZATION) {
            return `${scoreboardGTM}visualisations-button;visualisation`;
        }
        if (viewModes[1] === VIEW_MODE.STREAMING) {
            return `${scoreboardGTM}view-stream-button;view-stream`;
        }

        // if the next view mode is neither 'visualisations' nor 'streaming' then it should be 'hidden'.
        if (viewModes[0] === VIEW_MODE.VISUALIZATION) {
            return `${scoreboardGTM}close-visualisations-button;close-visualisations`;
        }
        if (viewModes[0] === VIEW_MODE.STREAMING) {
            return `${scoreboardGTM}close-stream-button;close-stream`;
        }

        // this should never be reached.
        return '';
    };

    return (<>
        {
            (isStreamingAvailable || isVisualizationAvailable) ?
            <>
                {
                    (isStreamingAvailable || isVisualizationReady) ?
                        <div
                            onClick={rotateViewModes}
                            className={toggleClassNames}
                            data-spectate-product={toggleGTM()}
                        />
                    : null
                }
                <div className='feed'>
                    <CollapsibleDiv isOpened={viewModes[0] !== VIEW_MODE.HIDDEN}>
                        {renderFeed()}
                    </CollapsibleDiv>
                </div>
            </>
            : null
        }
    </>);
};

ScoreboardFeedToggle.propTypes = {
    eventId: PropTypes.oneOfType([
        PropTypes.string.isRequired,
        PropTypes.number.isRequired,
    ]),
};

export default ScoreboardFeedToggle;
